import {compact} from '@benchling/js-utils';
import {Node, XYPosition} from 'reactflow';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {DefaultTaskSchemaNodeAdapter} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/DefaultTaskSchemaNodeAdapter';
import {NodeDetailsSidepanelContentProps} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {
  TaskSchemaNodeData,
  TaskSchemaStaticNodeData,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/TaskSchemaNodeRenderer';
import {TaskSchemaNodeRowRenderer} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/TaskSchemaNodeRowRenderer';
import {
  FLOWCHART_TASK_SECONDARY_INFO,
  FLOWCHART_OUTPUT_SECONDARY_INFO,
  ProcessFlowchartNodeType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {SimpleBenchlingNodeHandle} from 'ui-library/reactflow/nodes/SimpleBenchlingNode/sub-components/SimpleBenchlingNodeHandle';

import {ProcessTaskSchemaGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {SchemaInfo} from '../FieldMappingConstants';
import {generateTaskSchemaNodeIdentifier} from './flowchartIdentifierUtils';
import {
  NodeTransitionPosition,
  NodeTransitionPositionConfig,
} from './transitionModalWithFlowchartEditorConstants';

export function isFlowchartTaskSchema(schemaInfo: SchemaInfo): boolean {
  return schemaInfo.taskSchema.executionType === ProcessExecutionType.FLOWCHART;
}

/**
 * Gets the schema infos for available task nodes that can be added
 * given a flowchart task schema and list of nodes already present in the flowchart.
 */
export function getAvailableTaskSchemaInfos(
  allTaskSchemas: Array<ProcessTaskSchemaGraphQL>,
  nodes: Array<TaskSchemaStaticNodeData>,
  flowchartTaskSchema: ProcessTaskSchemaGraphQL,
  allowedExecutionTypes: Array<ProcessExecutionType>
): Array<SchemaInfo> {
  let schemaInfos: Array<SchemaInfo> = compact(
    allTaskSchemas.map((schema) => {
      if (!allowedExecutionTypes.includes(schema.executionType)) {
        return null;
      }
      return {
        taskSchema: schema,
        itemType: ModelTypeConstants.PROCESS_TASK,
      };
    })
  );

  // We add the root and output nodes to the list of available nodes, unless they are already present on the flowchart canvas
  if (!nodes.some((node) => node.nodeSecondaryInfo === FLOWCHART_OUTPUT_SECONDARY_INFO)) {
    schemaInfos = [
      {
        taskSchema: flowchartTaskSchema,
        itemType: ModelTypeConstants.PROCESS_OUTPUT,
      },
      ...schemaInfos,
    ];
  }
  if (!nodes.some((node) => node.nodeSecondaryInfo === FLOWCHART_TASK_SECONDARY_INFO)) {
    schemaInfos = [
      {
        taskSchema: flowchartTaskSchema,
        itemType: ModelTypeConstants.PROCESS_TASK,
      },
      ...schemaInfos,
    ];
  }
  return schemaInfos;
}

export interface FlowchartOptions {
  onNodeHeaderClick: (nodeId: string) => void;
  onFieldClick: (nodeId: string, fieldId: string) => void;
}

interface NodePosition {
  x: number;
  y: number;
}

/**
 * Generate data for node, using task schema node adapter to customize
 */
export function createStaticNodeData(
  schemaInfo: SchemaInfo,
  taskSchema: ProcessTaskSchemaGraphQL
): TaskSchemaStaticNodeData {
  const isFlowchartTask = isFlowchartTaskSchema(schemaInfo);
  const isFlowchartOutputNode = isFlowchartTask && schemaInfo.itemType !== ModelTypeConstants.PROCESS_TASK;
  // TODO: Use different adapters based on task schema type
  const adapter = new DefaultTaskSchemaNodeAdapter({isFlowchartOutput: isFlowchartOutputNode});
  return {
    id: generateTaskSchemaNodeIdentifier(taskSchema),
    nodeType: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
    nodeName: adapter.getNodeName({taskSchema}),
    /** Show secondary information to differentiate Flowchart Task/Output nodes from task schema nodes. */
    nodeSecondaryInfo: isFlowchartTask
      ? isFlowchartOutputNode
        ? FLOWCHART_OUTPUT_SECONDARY_INFO
        : FLOWCHART_TASK_SECONDARY_INFO
      : undefined,
    sections: adapter.createSections({taskSchema}),
    hideSectionSummaries: true,
  };
}

/**
 * Initial node positions within a transition.
 *
 * TODO: Use layout algorithm to determine node positions
 */
export function createNodePosition(nodeTransitionPositionConfig: NodeTransitionPositionConfig): NodePosition {
  const {nodeTransitionPosition, indexInNodeTransitionPosition} = nodeTransitionPositionConfig;
  const isSourceNodeInTransition = nodeTransitionPosition === NodeTransitionPosition.SOURCE;

  const initialRowSpacing = 200;
  const initialColumnSpacing = 400;
  const initialPosition = 0;

  return {
    x: initialPosition + (isSourceNodeInTransition ? initialPosition : initialColumnSpacing),
    y:
      initialPosition +
      (isSourceNodeInTransition ? indexInNodeTransitionPosition * initialRowSpacing : initialPosition),
  };
}

export const NODE_TYPE_TO_CREATE_NODE_FUNCTION: Record<
  ProcessFlowchartNodeType,
  (
    staticData: LogicNodeStaticData | TaskSchemaStaticNodeData,
    position: NodePosition,
    options: FlowchartOptions
  ) => Node<unknown>
> = {
  [ProcessFlowchartNodeType.TASK_SCHEMA_NODE]: createTaskNode,
  [ProcessFlowchartNodeType.ROUTER_NODE]: createRouterNode,
  [ProcessFlowchartNodeType.STOP_NODE]: createStopNode,
};

export interface LogicNodeStaticData {
  id: string;
  type: ProcessFlowchartNodeType;
  sections: Array<unknown>;
}

export function createRouterNode(
  staticData: LogicNodeStaticData,
  position: NodePosition,
  options: FlowchartOptions
): Node<unknown> {
  return {
    ...staticData,
    position,
    data: {
      ...staticData,
      onNodeHeaderClick: options.onNodeHeaderClick,
      // TODO: Add router node details sidepanel content
      nodeDetailsSidepanelContent: () => null,
      rowRenderer: () => null,
      nodeHeaderHandleRenderer: SimpleBenchlingNodeHandle,
    },
  };
}

export function createStopNode(
  staticData: LogicNodeStaticData,
  position: XYPosition,
  options: FlowchartOptions
): Node<unknown> {
  return {
    ...staticData,
    position,
    data: {
      ...staticData,
      rowRenderer: () => null,
      nodeHeaderHandleRenderer: SimpleBenchlingNodeHandle,
    },
  };
}

export function createTaskNode(
  staticData: TaskSchemaStaticNodeData,
  position: XYPosition,
  options: FlowchartOptions
): Node<TaskSchemaNodeData> {
  // TODO: Use different adapters based on task schema type
  const adapter = new DefaultTaskSchemaNodeAdapter({});
  return {
    id: staticData.id,
    type: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
    position,
    data: {
      ...staticData,
      onNodeHeaderClick: options.onNodeHeaderClick,
      nodeDetailsSidepanelContent: (props: NodeDetailsSidepanelContentProps) =>
        adapter.nodeDetailsSidepanelContent(props),
      onFieldClick: options.onFieldClick,
      rowRenderer: TaskSchemaNodeRowRenderer,
      nodeHeaderHandleRenderer: SimpleBenchlingNodeHandle,
    },
  };
}
